// Global Instructions Rule Applied!
import React, { forwardRef, useImperativeHandle, useState, useMemo, useCallback, useEffect, useRef, memo } from 'react'
import { useSelector } from 'react-redux'
import { InputManager } from '../../../core/components/InputManager/InputManager.tsx'
import { FieldConfig, FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import { apiRequest } from '../../../core/lib/apiRequest.js'
import dayjs from 'dayjs'
import {
  InteractionRecordFields,
  validateInteractionRecord
} from '../../../state_manager/data_models/interaction_record.ts'
import type { InteractionRecord } from '../../../state_manager/data_models/interaction_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { ACTION_TYPE_UPSERT } from '../../../state_manager/globals.ts'

/**
 * Parses a free-text name for a new contact from the contact picker (aligns with deals fieldConfigs).
 */
function parseInteractionContactName(name: string): { first_name: string | null; last_name: string } {
  if (!name || typeof name !== 'string') {
    return { first_name: null, last_name: '' }
  }
  const cleanName = name.replace(/\s*\(new\)\s*$/i, '').trim()
  if (!cleanName) {
    return { first_name: null, last_name: '' }
  }
  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0)
  if (nameParts.length === 0) {
    return { first_name: null, last_name: '' }
  }
  if (nameParts.length === 1) {
    return { first_name: null, last_name: nameParts[0] }
  }
  return {
    first_name: nameParts[0],
    last_name: nameParts.slice(1).join(' ')
  }
}

function isNewInteractionContact(contact: { label?: string; value?: string } | string | null | undefined): boolean {
  if (!contact || typeof contact !== 'object') return false
  return contact.value === 'new' || (contact.label != null && String(contact.label).includes('(new)'))
}

type ContactSelectValue = {
  label?: string
  value?: string
  email?: string | null
  data?: { originalSearch?: string }
}

function contactSelectToUserEntry(contact: string | ContactSelectValue): Record<string, unknown> {
  if (typeof contact === 'string') {
    return {
      [UserEntryFields.contacts__id]: contact,
      [UserEntryFields.contacts__email]: null,
      [UserEntryFields.prog__display_name]: null,
      [UserEntryFields.contacts__first_name]: null,
      [UserEntryFields.contacts__last_name]: null
    }
  }

  const label = contact.label || ''
  if (isNewInteractionContact(contact)) {
    const contactId = crypto.randomUUID()
    const nameSource = contact.data?.originalSearch || label || ''
    const { first_name, last_name } = parseInteractionContactName(nameSource)
    return {
      [UserEntryFields.contacts__id]: contactId,
      [UserEntryFields.contacts__email]: contact.email ?? null,
      [UserEntryFields.prog__display_name]: label || null,
      [UserEntryFields.contacts__first_name]: first_name ?? null,
      [UserEntryFields.contacts__last_name]: last_name || null,
      [UserEntryFields.prog__contacts__action]: ACTION_TYPE_UPSERT
    }
  }

  const nameParts = label.split(' ')
  const firstName = nameParts[0] || null
  const lastName = nameParts.slice(1).join(' ') || null

  return {
    [UserEntryFields.contacts__id]: contact.value || null,
    [UserEntryFields.contacts__email]: contact.email ?? null,
    [UserEntryFields.prog__display_name]: label || null,
    [UserEntryFields.contacts__first_name]: firstName,
    [UserEntryFields.contacts__last_name]: lastName
  }
}

interface InteractionFormValues {
  subject: string
  type: string
  date: string
  time: string
  datetime?: string
  owner: string | { label: string; value: string } | null
  participants: Array<string | { label: string; value: string }>
  description: string
  notes: string
}

export type InteractionType = 'note' | 'meeting' | 'email'

export interface InteractionFormProps {
  initialValues?: Partial<InteractionFormValues>
  onValuesChange?: (values: Partial<InteractionFormValues>) => void
  interactionType?: InteractionType
  onInteractionTypeChange?: (type: InteractionType) => void
  isAddMode?: boolean
  className?: string
  style?: React.CSSProperties
}

type CurrentUserContact = { label: string; value: string; email: string }

export interface InteractionFormRef {
  /** The validated interaction record built from the form (prefixed keys), not the raw field values. */
  getValues: () => InteractionRecord
  setValues: (values: Partial<InteractionFormValues>) => void
  validateFields: () => boolean
  resetFields: () => void
}

export const InteractionForm = forwardRef<InteractionFormRef, InteractionFormProps>(
  (
    {
      initialValues = {},
      onValuesChange,
      interactionType,
      onInteractionTypeChange,
      isAddMode = false,
      className = '',
      style = {}
    },
    ref
  ) => {
    // Parse datetime from initialValues if present, otherwise use current date/time
    const parseInitialValues = () => {
      const defaultDate = dayjs().format('YYYY-MM-DD')
      const defaultTime = dayjs().format('HH:mm:ss')

      // If datetime is provided in initialValues, parse it
      if (initialValues.datetime) {
        const parsed = dayjs(initialValues.datetime)
        if (parsed.isValid()) {
          return {
            date: parsed.format('YYYY-MM-DD'),
            time: parsed.format('HH:mm:ss')
          }
        }
      }

      return {
        date: defaultDate,
        time: defaultTime
      }
    }

    const { date: initialDate, time: initialTime } = parseInitialValues()

    const [fieldValues, setFieldValues] = useState<Partial<InteractionFormValues>>({
      subject: '',
      type: interactionType || 'note',
      date: initialDate,
      time: initialTime,
      owner: null,
      participants: [],
      description: '',
      notes: '',
      ...initialValues
    })

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

    useEffect(() => {
      if (interactionType && interactionType !== fieldValues.type) {
        setFieldValues((prev) => ({ ...prev, type: interactionType }))
      }
    }, [interactionType, fieldValues.type])

    // Resolve the logged-in user's contact record (matched by email) so new
    // interactions can default Owner and meeting participants to the creator.
    const authUserEmail = useSelector((state: any) => state.auth?.user?.email as string | undefined)
    const [currentUserContact, setCurrentUserContact] = useState<CurrentUserContact | null>(null)
    const autoAddedParticipantRef = useRef(false)

    // The rich text editor commits its controlled value on blur only, so a
    // save clicked straight after typing would read stale notes. The editor's
    // onLiveChange keeps this ref current on every keystroke; getValues()
    // prefers it over the blur-committed state.
    const liveNotesRef = useRef<string | null>(null)

    useEffect(() => {
      if (!isAddMode || !authUserEmail) {
        return
      }
      let cancelled = false
      const resolveCurrentUserContact = async () => {
        try {
          const params = new URLSearchParams({ search: authUserEmail })
          const response = await apiRequest(`/contacts/select?${params}`)
          if (cancelled || !response?.success || !Array.isArray(response.data)) {
            return
          }
          const match = response.data.find(
            (contact: { email?: string }) => (contact.email || '').toLowerCase() === authUserEmail.toLowerCase()
          )
          if (match) {
            setCurrentUserContact({ label: match.label, value: match.value, email: match.email || '' })
          }
        } catch {
          // No contact match for the user — leave Owner/participants for manual entry
        }
      }
      resolveCurrentUserContact()
      return () => {
        cancelled = true
      }
    }, [isAddMode, authUserEmail])

    // Default Owner to the creating user (only while the field is still empty)
    useEffect(() => {
      if (!isAddMode || !currentUserContact) {
        return
      }
      setFieldValues((prev) => (prev.owner ? prev : { ...prev, owner: currentUserContact }))
    }, [isAddMode, currentUserContact])

    // For meetings, automatically add the creating user as a participant (once
    // per form instance, so a deliberate removal is not overridden)
    useEffect(() => {
      if (!isAddMode || !currentUserContact || fieldValues.type !== 'meeting' || autoAddedParticipantRef.current) {
        return
      }
      autoAddedParticipantRef.current = true
      setFieldValues((prev) => {
        const participants = prev.participants || []
        const alreadyPresent = participants.some(
          (participant) =>
            (typeof participant === 'object' ? participant?.value : participant) === currentUserContact.value
        )
        if (alreadyPresent) {
          return prev
        }
        return { ...prev, participants: [...participants, currentUserContact] }
      })
    }, [isAddMode, currentUserContact, fieldValues.type])

    const handleFieldChange = useCallback(
      (key: string, value: any) => {
        setFieldValues((prev) => {
          const newValues = { ...prev, [key]: value }

          if (key === 'type') {
            onInteractionTypeChange?.(value)
          }

          // Combine date and time into datetime when either changes
          if (key === 'date' || key === 'time') {
            const date = key === 'date' ? value : newValues.date
            const time = key === 'time' ? value : newValues.time
            if (date && time) {
              newValues.datetime = `${date} ${time}`
            } else {
              delete newValues.datetime
            }
          }

          // Clear validation error for this field when it changes
          setValidationErrors((prev) => {
            const newErrors = { ...prev }
            delete newErrors[key]
            return newErrors
          })

          onValuesChange?.(newValues)
          return newValues
        })
      },
      [onValuesChange, onInteractionTypeChange]
    )

    const validateFields = useCallback((): boolean => {
      const { subject, type, date, owner } = fieldValues
      const errors: Record<string, string> = {}

      if (!subject || subject.trim() === '') {
        errors.subject = 'Subject is required'
      }

      if (!type) {
        errors.type = 'Type is required'
      }

      if (!date) {
        errors.date = 'Date is required'
      }

      if (!owner) {
        errors.owner = 'Owner is required'
      }

      setValidationErrors(errors)
      return Object.keys(errors).length === 0
    }, [fieldValues])

    useImperativeHandle(ref, () => ({
      getValues: () => {
        const { subject, type, date, time, owner, participants, description } = fieldValues
        const notes = liveNotesRef.current ?? fieldValues.notes

        // Transform owner to UserEntry format
        let ownerEntry = null
        if (owner) {
          ownerEntry = contactSelectToUserEntry(owner as string | ContactSelectValue)
        }

        // Transform participants array to UserEntry format
        const participantsEntries = (participants || []).map((contact) => contactSelectToUserEntry(contact))

        // Combine date and time into datetime with timezone offset (ISO 8601 format)
        let datetimeValue = null
        if (date && time) {
          const combinedDateTime = dayjs(`${date} ${time}`)
          if (combinedDateTime.isValid()) {
            // Format as ISO 8601 with timezone offset: YYYY-MM-DDTHH:mm:ss±HH:mm
            // dayjs format 'Z' gives +00:00 format, 'ZZ' gives +0000 format
            datetimeValue = combinedDateTime.format('YYYY-MM-DDTHH:mm:ssZ')
          }
        }

        // Return object with prefixed field names using InteractionRecordFields constants
        const interactionRecord = {
          [InteractionRecordFields.interactions__id]: crypto.randomUUID(),
          [InteractionRecordFields.interactions__subject]: subject || '',
          [InteractionRecordFields.interactions__type]: type || 'note',
          [InteractionRecordFields.interactions__datetime]: datetimeValue || null,
          [InteractionRecordFields.interactions__description]: description || '',
          [InteractionRecordFields.interactions__owner]: ownerEntry,
          [InteractionRecordFields.prog__interaction_participants]: participantsEntries,
          [InteractionRecordFields.interactions__notes]: notes || ''
        }

        // Validate using schema before returning
        try {
          return validateInteractionRecord(interactionRecord)
        } catch (error) {
          throw new Error(`Interaction validation failed: ${error instanceof Error ? error.message : String(error)}`)
        }
      },
      setValues: (values: Partial<InteractionFormValues>) => {
        if ('notes' in values) {
          liveNotesRef.current = null
        }
        // If datetime is provided, parse it into date and time
        if (values.datetime) {
          const parsed = dayjs(values.datetime)
          if (parsed.isValid()) {
            setFieldValues((prev) => ({
              ...prev,
              ...values,
              date: parsed.format('YYYY-MM-DD'),
              time: parsed.format('HH:mm:ss')
            }))
            return
          }
        }
        setFieldValues((prev) => ({ ...prev, ...values }))
      },
      validateFields,
      resetFields: () => {
        autoAddedParticipantRef.current = false
        liveNotesRef.current = null
        setFieldValues({
          subject: '',
          type: 'note',
          date: dayjs().format('YYYY-MM-DD'),
          time: dayjs().format('HH:mm:ss'),
          owner: isAddMode && currentUserContact ? currentUserContact : null,
          participants: [],
          description: '',
          notes: ''
        })
        setValidationErrors({})
      }
    }))

    const nonNotesFields: FieldConfig[] = useMemo(() => {
      const currentType = fieldValues.type || 'note'
      const showParticipants = currentType === 'meeting' || currentType === 'email'

      const baseFields: FieldConfig[] = [
        {
          key: 'subject',
          type: FieldTypeEnum.TEXTAREA,
          value: fieldValues.subject || '',
          required: true,
          placeholder: 'Add interaction subject here',
          maxLength: 80,
          autoSize: { minRows: 1, maxRows: 4 },
          size: 'middle',
          autoFocus: true,
          labelPosition: 'none', // Subject has no label
          className: 'mt-1' // Preserve original top margin
        },
        {
          key: 'type',
          label: 'Type',
          type: FieldTypeEnum.SELECT,
          value: fieldValues.type || 'note',
          required: true,
          placeholder: 'Select interaction type...',
          size: 'small',
          options: [
            { label: 'Meeting', value: 'meeting' },
            { label: 'Email', value: 'email' },
            { label: 'Note', value: 'note' }
          ]
        },
        {
          key: 'date',
          label: 'Date',
          type: FieldTypeEnum.DATE,
          value: fieldValues.date || dayjs().format('YYYY-MM-DD'),
          required: true,
          format: 'YYYY-MM-DD',
          size: 'small',
          allowClear: false,
          groupWithNext: true // Group with Time field for side-by-side layout
        },
        {
          key: 'time',
          label: 'Time',
          type: FieldTypeEnum.TIME,
          value: fieldValues.time || dayjs().format('HH:mm:ss'),
          required: false,
          format: 'HH:mm:ss',
          size: 'small',
          allowClear: false
        },
        {
          key: 'owner',
          label: 'Owner',
          type: FieldTypeEnum.CONTACT,
          value: fieldValues.owner || null,
          required: true,
          placeholder: 'Select owner',
          mode: 'single',
          allowAddNew: true,
          size: 'small'
        }
      ]

      if (showParticipants) {
        baseFields.push({
          key: 'participants',
          label: 'Participants',
          type: FieldTypeEnum.CONTACT,
          value: fieldValues.participants || [],
          required: false,
          placeholder: 'Search participants...',
          mode: 'multiple',
          allowAddNew: true,
          size: 'small'
        })
      }

      baseFields.push({
        key: 'description',
        label: 'Description',
        type: FieldTypeEnum.TEXTAREA,
        value: fieldValues.description || '',
        required: false,
        rows: 1,
        autoSize: { minRows: 1, maxRows: 4 },
        size: 'small'
      })

      return baseFields
    }, [
      fieldValues.subject,
      fieldValues.type,
      fieldValues.date,
      fieldValues.time,
      fieldValues.owner,
      fieldValues.participants,
      fieldValues.description
    ])

    const handleNotesLiveChange = useCallback((html: string) => {
      liveNotesRef.current = html
    }, [])

    const notesField: FieldConfig = useMemo(
      () => ({
        key: 'notes',
        label: 'Notes',
        type: FieldTypeEnum.RICHTEXT,
        value: fieldValues.notes || '',
        required: false,
        placeholder: 'Enter interaction notes...',
        onLiveChange: handleNotesLiveChange,
        className: 'flex-1',
        style: { display: 'flex', flexDirection: 'column', minHeight: 0 }
      }),
      [fieldValues.notes, handleNotesLiveChange]
    )

    const notesValidationErrors = useMemo((): Record<string, string> => {
      const notesError = validationErrors.notes
      return notesError ? { notes: notesError } : {}
    }, [validationErrors.notes])

    const notesFieldsArray = useMemo(() => [notesField], [notesField])

    const notesInputStyle = useMemo<React.CSSProperties>(
      () => ({ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }),
      []
    )

    return (
      <div className={`flex flex-col pr-1 ${className}`} style={style}>
        <div className='flex-none'>
          {/* All non-notes fields in a single InputManager */}
          <div className='mt-1'>
            <InputManager
              inputMode='edit'
              fields={nonNotesFields}
              labelPosition='above'
              onChange={handleFieldChange}
              fieldWrapping={true}
              validationErrors={validationErrors}
            />
          </div>
        </div>

        {/* Notes field - takes up remaining space */}
        <div
          className='flex-1 flex flex-col min-h-0 mt-2'
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <div className='mb-2'>
            <span className='text-sm font-bold text-gray-800'>Notes:</span>
          </div>
          <div
            className='flex-1 min-h-0'
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
          >
            <NotesInputManager
              fields={notesFieldsArray}
              onChange={handleFieldChange}
              validationErrors={notesValidationErrors}
              className='h-full'
              style={notesInputStyle}
            />
          </div>
        </div>
      </div>
    )
  }
)

InteractionForm.displayName = 'InteractionForm'

// Memoized wrapper for notes InputManager to prevent re-renders when other fields change
const NotesInputManager = memo<{
  fields: FieldConfig[]
  onChange: (key: string, value: any) => void
  validationErrors: Record<string, string>
  className?: string
  style?: React.CSSProperties
}>(
  ({ fields, onChange, validationErrors, className, style }) => {
    return (
      <InputManager
        inputMode='edit'
        fields={fields}
        labelPosition='none'
        onChange={onChange}
        className={className}
        style={style}
        fieldWrapping={true}
        validationErrors={validationErrors}
      />
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if notes-specific props change
    return (
      prevProps.fields === nextProps.fields &&
      prevProps.onChange === nextProps.onChange &&
      prevProps.validationErrors === nextProps.validationErrors &&
      prevProps.className === nextProps.className &&
      prevProps.style === nextProps.style
    )
  }
)

NotesInputManager.displayName = 'NotesInputManager'
